import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resets scroll to the top of the page on every route change.
 *
 * React Router v6 with BrowserRouter does no scroll management.
 * That means clicking a link from the bottom of one page lands the
 * user at the bottom of the next page — looks like a broken page.
 *
 * Two cooperating fixes:
 *
 * 1. Tell the browser to stop trying to restore scroll itself.
 *    `history.scrollRestoration = 'manual'` opts out of the
 *    browser's well-intentioned auto-restore on forward/back. We
 *    own scroll position from this point.
 *
 * 2. On every pathname change, scrollTo(0, 0) immediately, then
 *    once more via setTimeout(0). The second call covers the case
 *    where a Suspense fallback resolves and the lazy chunk's content
 *    grows the document AFTER the first scroll — the height change
 *    can occasionally nudge scroll position. Calling at the next
 *    macrotask guarantees we end at top.
 *
 * Hash navigations (`/#features`) are intentionally skipped — they
 * already have their own scroll target via useScrollToLandingHash.
 *
 * Mount once inside <Router> but outside <Routes>.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
    const id = window.setTimeout(() => window.scrollTo(0, 0), 0);
    return () => window.clearTimeout(id);
  }, [pathname, hash]);

  return null;
}
